function AboutPage() {
  return (
    <main className="info-page">
      <section className="info-hero">
        <div className="info-hero-copy">
          <p className="eyebrow">Formaslov</p>
          <h1>О проекте</h1>
          <p className="info-lead">
            Formaslov — это MVP веб-платформы для ручной разметки и
            семантического кодирования русскоязычных текстов.
          </p>
          <p>
            Сейчас проект реализует базовый рабочий сценарий: пользователь
            может загрузить материалы вручную или ZIP-архивом, создать
            собственные метки, выделить фрагменты, назначить им смысловые
            категории и экспортировать результат в JSON.
          </p>
        </div>

        <aside className="highlight-box">
          <span className="highlight-label">Текущий этап</span>
          <strong>
            MVP будущей платформы для качественного анализа русскоязычных
            текстов
          </strong>
          <p>
            Эта версия показывает основу продукта: удобную работу с текстом,
            хранение разметки и подготовку данных для дальнейшего анализа.
          </p>
        </aside>
      </section>

      <section className="info-section">
        <div className="info-section-heading">
          <p className="eyebrow">Контекст</p>
          <h2>Зачем нужен Formaslov</h2>
        </div>
        <div className="info-prose">
          <p>
            В качественных исследованиях, маркетинговой аналитике,
            образовательных и NLP-задачах часто приходится работать с большими
            массивами текстов: интервью, открытыми ответами, отзывами,
            комментариями, публикациями, статьями и пользовательскими
            сообщениями.
          </p>
          <p>
            Полностью ручная разметка в таблицах или текстовых редакторах быстро
            становится неудобной: сложно поддерживать единую систему меток,
            хранить связи между фрагментами, возвращаться к уже размеченным
            участкам и экспортировать результат в пригодном для анализа виде.
          </p>
          <p>
            Полностью автоматическая обработка тоже не всегда подходит:
            алгоритмы могут ошибаться в контексте, иронии, эмоциональной
            окраске, культурных отсылках и сложных смысловых связях. Для
            русского языка это особенно важно из-за богатой морфологии,
            свободного порядка слов, неоднозначности формулировок и высокой
            зависимости смысла от контекста.
          </p>
          <div className="info-callout">
            Formaslov сохраняет ведущую роль исследователя, но помогает
            структурировать ручную работу и переводить её результат в
            машинно-читаемый формат.
          </div>
        </div>
      </section>

      <section className="info-section">
        <div className="info-section-heading">
          <p className="eyebrow">Аудитория</p>
          <h2>Для кого этот проект</h2>
        </div>
        <div className="info-card-grid audience-grid">
          <article className="info-card">
            <h3>Исследователи</h3>
            <p>
              Работа с интервью, открытыми ответами и текстовыми корпусами.
            </p>
          </article>
          <article className="info-card">
            <h3>Образование</h3>
            <p>
              Преподаватели и студенты, изучающие методы качественного анализа.
            </p>
          </article>
          <article className="info-card">
            <h3>Аналитики</h3>
            <p>
              Разбор отзывов, комментариев, публикаций и пользовательских
              сообщений.
            </p>
          </article>
          <article className="info-card">
            <h3>Команды данных</h3>
            <p>
              Подготовка размеченных данных для последующего анализа или
              NLP-задач.
            </p>
          </article>
          <article className="info-card">
            <h3>Разработчики моделей</h3>
            <p>
              Сбор датасетов для проверки или обучения моделей обработки
              текста.
            </p>
          </article>
        </div>
      </section>

      <section className="info-section">
        <div className="info-section-heading">
          <p className="eyebrow">Рабочая версия</p>
          <h2>Что уже умеет MVP</h2>
        </div>
        <div className="info-card info-list-card">
          <ul className="info-check-list">
            <li>Регистрация и работа со своими документами.</li>
            <li>
              Загрузка <code>.txt</code>-файла, создание документа вручную и
              пакетный импорт ZIP с <code>.txt</code>/<code>.docx</code>.
            </li>
            <li>Создание собственных меток.</li>
            <li>Выделение фрагментов текста и назначение им меток.</li>
            <li>Подсветка размеченных фрагментов.</li>
            <li>Просмотр списка аннотаций.</li>
            <li>Удаление аннотаций и управление метками.</li>
            <li>Фоновый экспорт документа, меток и аннотаций в JSON.</li>
            <li>Демо-режим без регистрации с примером разметки.</li>
          </ul>
        </div>
        <p className="info-note">
          Данные пользователей изолированы: каждый пользователь видит только
          свои документы, метки и аннотации. Демо-режим работает только для
          просмотра и не изменяет реальные пользовательские данные.
        </p>
      </section>

      <section className="info-section">
        <div className="info-section-heading">
          <p className="eyebrow">Перспектива</p>
          <h2>Куда проект может развиваться</h2>
        </div>
        <p className="info-section-intro">
          Текущий MVP — это первый шаг к более полноценной платформе для
          качественного анализа русскоязычных текстов.
        </p>
        <div className="future-grid">
          <ul className="info-bullet-list">
            <li>Иерархические коды и группы меток.</li>
            <li>Заметки, комментарии и исследовательские мемо.</li>
            <li>Поиск и фильтрация по аннотациям, меткам и документам.</li>
            <li>Статистика по кодам, частотам и пересечениям.</li>
            <li>Командная работа над корпусами текстов.</li>
          </ul>
          <ul className="info-bullet-list">
            <li>Импорт и экспорт в дополнительные форматы.</li>
            <li>Поддержка больших текстовых коллекций.</li>
            <li>Полуавтоматические подсказки для разметки.</li>
            <li>Инструменты, учитывающие специфику русского языка.</li>
            <li>Подготовка датасетов для NLP-моделей.</li>
          </ul>
        </div>
        <div className="info-closing">
          <p>
            В долгосрочной перспективе Formaslov задуман как русскоязычная
            веб-адаптация инструментов для качественного анализа данных — по
            духу близкая к решениям вроде QualCoder, но с фокусом на русский
            язык, российский исследовательский контекст и задачи семантической
            разметки русскоязычных текстов.
          </p>
          <strong>
            Идея Formaslov — не заменить исследователя автоматикой, а дать ему
            удобный инструмент для внимательного чтения, смысловой разметки и
            дальнейшей аналитической работы.
          </strong>
        </div>
      </section>
    </main>
  );
}

export default AboutPage;
